/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
// store
import {
  setAuthStatus,
  setUserLoggedOut,
} from "../../store/actionCreators/userDataActionCreator";
import {
  setCookieExisted,
  setInitialized,
} from "../../store/actionCreators/appActionCreator";
// hooks
import useMakeRequest from "../../hooks/useMakeRequest";
import eraseCookie from "../../hooks/eraseCookie";
// components
import Preloader from "../../components/UI/Preloader/Preloader";
import NewButton from "../../components/UI/Button/NewButton";
// styles
import classes from "./ChangePassword.module.sass";

const SUBMIT_TIMER_DURATION = 120;

const formConfig = [
  { label: "Current Password", fieldName: "currentPassword" },
  { label: "New Password", fieldName: "newPassword" },
  { label: "Repeat New Password", fieldName: "repeatPassword" },
];

function ChangePassword({ setIsAuth, setLoggedOut, setInitialize, setCookie }) {
  const history = useHistory();

  const [passwordsVisibility, setPasswordsVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    repeatPassword: false,
  });
  const [isSubmit, setIsSubmit] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [timerLeft, setTimerLeft] = useState(0);
  const setSubmittingRef = useRef(null);

  const [setParams, data, isLoading, hasError, error, setResult] =
    useMakeRequest();

  const validatePasswordForm = (values) => {
    const errors = {};

    const regEx = /(?=.*[0-9])(?=.*[a-zA-Z])[0-9a-zA-Z!@#$%^&*]{6,}/g;

    if (!values.currentPassword) {
      errors.currentPassword = "All fields are required";
      //  agreed with backend to not validate the old password as the new one
    }

    if (!values.newPassword) {
      errors.newPassword = "All fields are required";
    } else if (values.newPassword.length < 6) {
      errors.newPassword = "The minimum password length must be 6 characters";
    } else if (!values.newPassword.match(regEx)) {
      errors.newPassword =
        "Invalid new password. The password must contain numbers and letters";
    }

    if (!values.repeatPassword) {
      errors.repeatPassword = "All fields are required";
    } else if (values.repeatPassword !== values.newPassword) {
      errors.repeatPassword = "New passwords are different";
    } else if (!values.repeatPassword.match(regEx)) {
      errors.repeatPassword = "Invalid field, repeat new password";
    }
    return errors;
  };

  const onSubmitForm = (values) => {
    setResult(null);
    setParams({
      c: "accountStore",
      a: "changePassword",
      p: {
        password_old: values.currentPassword,
        password_new: values.newPassword,
      },
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  function handleLogout() {
    eraseCookie("aAuth");
    setIsAuth(false);
    setLoggedOut();
    setInitialize(false);
    setCookie(false);
    history.push("/login?forgot=true");
  }

  useEffect(() => {
    if (data) {
      setIsSubmit(false);
      setSubmittingRef.current?.(false);
      if (!error) {
        setIsSuccess(true);
        setTimerLeft(SUBMIT_TIMER_DURATION);
      }
    }
  }, [data, error]);

  useEffect(() => {
    if (timerLeft > 0) {
      const timeoutId = setTimeout(() => {
        setTimerLeft((prev) => prev - 1);
      }, 1000);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    setIsSuccess(false);

    return undefined;
  }, [timerLeft]);

  return (
    <article className={classes.changePassword}>
      <div className={classes.changePassword_block}>
        <div className={classes.changePassword_title}>
          <h3>Change Password</h3>
        </div>
        <Formik
          initialValues={{
            currentPassword: "",
            newPassword: "",
            repeatPassword: "",
          }}
          validate={(values) => validatePasswordForm(values)}
          onSubmit={(values, { setSubmitting }) => {
            setSubmittingRef.current = setSubmitting;
            onSubmitForm(values);
          }}
          validateOnChange={!!isSubmit}
          validateOnBlur={!!isSubmit}
        >
          {({ isSubmitting, errors, isValid }) => {
            return (
              <>
                <Form className={classes.changePasswordForm}>
                  <div
                    className={`${classes.changePassword_message} ${classes.changePassword_message_validation}`}
                  >
                    For your security, please choose a password with at least{" "}
                    <strong>6 characters</strong>, including{" "}
                    <strong>letters and numbers</strong>.
                  </div>

                  <div
                    className={`${classes.changePassword_message} ${
                      (!isValid && errors) || (error && hasError)
                        ? classes.changePassword_message_error
                        : ""
                    } ${
                      isSubmitting && isLoading
                        ? classes.changePassword_preloader
                        : ""
                    } ${
                      isSuccess && timerLeft
                        ? classes.changePassword_message_success
                        : ""
                    }`}
                  >
                    {!isValid && errors ? (
                      errors.newPassword ||
                      errors.repeatPassword ||
                      errors.currentPassword
                    ) : isSuccess && timerLeft ? (
                      "Password changed successfully!"
                    ) : error && hasError ? (
                      error
                    ) : isSubmitting && isLoading ? (
                      <Preloader width="35px" />
                    ) : (
                      ""
                    )}
                  </div>

                  <div className={classes.changePasswordForm_wrap}>
                    {formConfig.map((field, index) => (
                      <div
                        key={index}
                        className={[classes.changePasswordForm_line].join(" ")}
                      >
                        <label>{field.label}</label>
                        <div
                          className={`${
                            classes.changePasswordForm_input_wrapper
                          } ${
                            errors[field.fieldName]
                              ? classes.changePasswordForm_input_wrapper_error
                              : ""
                          }`}
                        >
                          <Field
                            name={field.fieldName}
                            type={
                              passwordsVisibility[field.fieldName]
                                ? "text"
                                : "password"
                            }
                            className={classes.changePasswordForm_input}
                            onInput={() => setIsSuccess(false)}
                          />
                          <span
                            onClick={() =>
                              setPasswordsVisibility((prev) => {
                                return {
                                  ...prev,
                                  [field.fieldName]:
                                    !passwordsVisibility[field.fieldName],
                                };
                              })
                            }
                            aria-hidden="true"
                            className={
                              passwordsVisibility[field.fieldName]
                                ? `${classes.passVisibility} ${classes.passVisibility_v}`
                                : classes.passVisibility
                            }
                          />
                        </div>
                      </div>
                    ))}
                    <div className={classes.changePasswordForm_line}>
                      <label />
                      <NewButton
                        className="btn_new--primary"
                        type="submit"
                        disabled={(isLoading && isSubmitting) || timerLeft > 0}
                        onClick={() => setIsSubmit(true)}
                      >
                        <span>Change Password</span>{" "}
                      </NewButton>
                    </div>
                    <div className={classes.changePasswordForm_line}>
                      <label />
                      <button
                        type="button"
                        className={classes.changePasswordForm_forgot}
                        onClick={() => handleLogout()}
                      >
                        Forgot Password?
                      </button>
                    </div>
                  </div>
                </Form>
              </>
            );
          }}
        </Formik>
        {timerLeft > 0 && (
          <div className={classes.changePassword_warning}>
            To protect your account, there is a waiting period between password
            changes. You can change it again in{" "}
            <span className={classes.changePassword_timer}>
              {formatTime(timerLeft)}
            </span>
          </div>
        )}
      </div>
    </article>
  );
}
ChangePassword.propTypes = {
  setIsAuth: PropTypes.func.isRequired,
  setLoggedOut: PropTypes.func.isRequired,
  setInitialize: PropTypes.func.isRequired,
  setCookie: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    isAuth: state.userDataStore.isAuth,
  };
};

const mapDispatchToProps = {
  setIsAuth: setAuthStatus,
  setLoggedOut: setUserLoggedOut,
  setInitialize: setInitialized,
  setCookie: setCookieExisted,
};
export default connect(mapStateToProps, mapDispatchToProps)(ChangePassword);
